import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Crown, Hexagon } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Landing-page pricing section.
 *
 * Three columns (Free, Pro, Team). Pro is highlighted as "Most popular".
 * A monthly/yearly toggle switches the prices in real time; the toggle
 * sits between the heading and the cards.
 */

const TIERS = [
  {
    key: "free",
    label: "Free",
    tagline: "Track your first hive.",
    price: { month: 0, year: 0 },
    icon: Hexagon,
    accent: "from-stone-50 to-amber-50/30 border-stone-200",
    iconBg: "bg-stone-100 text-stone-700",
    cta: { label: "Start free", to: "/register" },
    features: [
      "1 hive yard / apiary",
      "Unlimited inspection journals",
      "5 AI diagnoses / month",
      "10 video uploads / month",
      "1 co-keeper invite",
      "Photo gallery & basic reminders",
      "Full members-only beekeeping guide",
      "Community forum access",
      "Email support",
      "Hive sharing — invite a co-keeper or friend",
    ],
    note: "No credit card required.",
  },
  {
    key: "pro",
    label: "Pro",
    tagline: "Everything a serious hobbyist needs.",
    price: { month: 12, year: 115 },
    icon: Sparkles,
    accent: "from-amber-50 to-yellow-50 border-amber-300 ring-2 ring-amber-200",
    iconBg: "bg-amber-500 text-stone-950",
    cta: { label: "Start 14-day trial", to: "/register" },
    badge: "Most popular",
    features: [
      "Everything in Free, plus:",
      "Unlimited AI diagnoses & video uploads",
      "Unlimited yards & multi-apiary support",
      "Smart Calendar — seasonal predictive nudges",
      "Voice-to-Journal — dictate while you inspect",
      "Hive Health Trends dashboard",
      "Compare hives side-by-side",
      "Treatments & feeding tracker with follow-ups",
      "Weather-aware Inspection Forecast",
      "CSV exports + printable annual report",
      "10 co-keeper invites",
    ],
    note: "14-day free trial. Cancel anytime.",
  },
  {
    key: "team",
    label: "Team",
    tagline: "For bee clubs & multi-keeper apiaries.",
    price: { month: 29, year: 278 },
    icon: Crown,
    accent: "from-stone-900 to-stone-800 border-stone-800 text-amber-50",
    iconBg: "bg-amber-400 text-stone-950",
    cta: { label: "Get team access", to: "/register" },
    features: [
      "Everything in Pro, plus:",
      "Unlimited co-keeper invites",
      "Apiary-wide collaboration",
      "Priority email support",
    ],
    note: "Pay annually and save 20%.",
    dark: true,
  },
];

export default function LandingPricing() {
  const [period, setPeriod] = useState("month"); // "month" | "year"
  return (
    <section
      id="spy-pricing"
      className="relative max-w-6xl mx-auto px-6 py-24 sm:py-32"
      data-testid="landing-pricing"
    >
      <header className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-700 mb-3">Plans & pricing</div>
        <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-stone-900">
          One plan for every stage of your beekeeping.
        </h2>
        <p className="text-stone-600 mt-4 text-base sm:text-lg">
          Start free, upgrade when you outgrow it. Pro and Team include a 14-day trial — no credit card required to try.
        </p>
      </header>

      <BillingToggle period={period} onChange={setPeriod} />

      <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-10" data-testid="pricing-grid">
        {TIERS.map((t) => <TierCard key={t.key} tier={t} period={period} />)}
      </div>

      <ComparisonStrip />

      <p className="text-center text-xs text-stone-500 mt-10">
        Prices in USD. Sales tax may apply. Existing customers keep their current price for the lifetime of the subscription.
      </p>
    </section>
  );
}

function BillingToggle({ period, onChange }) {
  return (
    <div className="flex items-center justify-center" data-testid="billing-toggle">
      <div className="inline-flex items-center bg-stone-100 border border-stone-200 rounded-full p-1 gap-1">
        <button
          type="button"
          onClick={() => onChange("month")}
          className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${
            period === "month" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"
          }`}
          data-testid="billing-toggle-month"
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => onChange("year")}
          className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors inline-flex items-center gap-2 ${
            period === "year" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"
          }`}
          data-testid="billing-toggle-year"
        >
          Yearly
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
            Save 20%
          </span>
        </button>
      </div>
    </div>
  );
}

function TierCard({ tier, period }) {
  const { label, tagline, price, icon: Icon, accent, iconBg, cta, features, note, badge, dark } = tier;
  const isFree = price.month === 0;
  // Show monthly-equivalent on the yearly view so cards stay visually consistent
  // ($12/mo vs $115/yr → $9.58/mo when yearly). We render the headline figure
  // and a sub-line with the full annual total.
  const monthlyEquivalent = period === "year" && price.year ? price.year / 12 : price.month;
  const displayed = period === "year" && price.year ? monthlyEquivalent : price.month;

  return (
    <div
      className={`relative rounded-3xl border bg-gradient-to-br ${accent} p-7 flex flex-col`}
      data-testid={`pricing-tier-${tier.key}`}
    >
      {badge && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full bg-stone-900 text-amber-200 border border-amber-300"
          data-testid={`pricing-badge-${tier.key}`}
        >
          {badge}
        </span>
      )}

      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h3 className={`font-display text-2xl font-medium ${dark ? "text-amber-50" : "text-stone-900"}`}>{label}</h3>
      </div>

      <p className={`text-sm ${dark ? "text-stone-300" : "text-stone-600"} mb-6`}>{tagline}</p>

      <div className="mb-6 min-h-[88px]">
        {isFree ? (
          <div className={`font-display ${dark ? "text-amber-50" : "text-stone-900"}`}>
            <span className="text-5xl font-bold tracking-tight">$0</span>
            <span className={`text-sm ${dark ? "text-stone-400" : "text-stone-500"} ml-1`}>forever</span>
          </div>
        ) : (
          <div className={`font-display ${dark ? "text-amber-50" : "text-stone-900"}`}>
            <div className="flex items-baseline gap-1">
              <span className="text-5xl font-bold tracking-tight tabular-nums" data-testid={`pricing-amount-${tier.key}`}>
                ${displayed % 1 === 0 ? displayed : displayed.toFixed(2)}
              </span>
              <span className={`text-sm ${dark ? "text-stone-400" : "text-stone-500"}`}>/ month</span>
            </div>
            {period === "year" && price.year && (
              <div className={`text-xs mt-1 ${dark ? "text-amber-200" : "text-amber-700"} font-medium`} data-testid={`pricing-year-total-${tier.key}`}>
                Billed ${price.year}/year · save ${(price.month * 12) - price.year}
              </div>
            )}
          </div>
        )}
      </div>

      <ul className={`space-y-2.5 text-sm ${dark ? "text-stone-200" : "text-stone-700"} flex-1`} data-testid={`pricing-features-${tier.key}`}>
        {features.map((f, i) => {
          const isHeader = f.startsWith("Everything in");
          return (
            <li key={i} className={`flex items-start gap-2.5 ${isHeader ? "font-semibold pt-1" : ""}`}>
              {isHeader ? (
                <ArrowRight className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-amber-300" : "text-amber-700"}`} />
              ) : (
                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-amber-300" : "text-amber-600"}`} />
              )}
              <span>{f}</span>
            </li>
          );
        })}
      </ul>

      <div className="mt-8">
        <Link to={cta.to}>
          <Button
            size="lg"
            className={`w-full rounded-full font-bold ${
              tier.key === "pro"
                ? "bg-amber-500 hover:bg-amber-400 text-stone-950"
                : dark
                  ? "bg-amber-400 hover:bg-amber-300 text-stone-950"
                  : "bg-stone-900 hover:bg-stone-800 text-amber-50"
            }`}
            data-testid={`pricing-cta-${tier.key}`}
          >
            {cta.label} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
        <p className={`text-[11px] text-center mt-3 ${dark ? "text-stone-400" : "text-stone-500"}`}>{note}</p>
      </div>
    </div>
  );
}

function ComparisonStrip() {
  return (
    <div className="mt-14 grid sm:grid-cols-3 gap-4 text-center" data-testid="pricing-trust-strip">
      <Trust title="Switch anytime" body="Upgrade, downgrade or cancel without a phone call. Your data stays put." />
      <Trust title="No data lock-in" body="Export everything as CSV whenever you want — your records are yours." />
      <Trust title="Made by beekeepers" body="Every Pro feature is a tool we wished we had during our own inspections." />
    </div>
  );
}

function Trust({ title, body }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white/50 p-5">
      <div className="text-sm font-display font-medium text-stone-900">{title}</div>
      <div className="text-xs text-stone-600 mt-1.5 leading-relaxed">{body}</div>
    </div>
  );
}
